'use client';

import React, { PropsWithChildren, useCallback } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import PaginationControls from './PaginationControls';

const DEFAULT_ARTICLES_PER_PAGE = 9;

interface ArticleGridPaginatedProps extends PropsWithChildren {
  articlesPerPage?: number;
}

function buildUrl(pathname: string, params: URLSearchParams, page: number): string {
  const next = new URLSearchParams(params);
  if (page <= 1) {
    next.delete('page');
  } else {
    next.set('page', String(page));
  }
  const qs = next.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

const ArticleGridPaginated = ({ children, articlesPerPage = DEFAULT_ARTICLES_PER_PAGE }: ArticleGridPaginatedProps) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const allChildren = React.Children.toArray(children);
  const totalPages = Math.max(1, Math.ceil(allChildren.length / articlesPerPage));
  const rawPage = Number(searchParams.get('page')) || 1;
  const currentPage = Math.min(Math.max(1, rawPage), totalPages);

  const startIdx = (currentPage - 1) * articlesPerPage;
  const pageChildren = allChildren.slice(startIdx, startIdx + articlesPerPage);

  const onPageChange = useCallback(
    (page: number) => {
      const url = buildUrl(pathname, searchParams, page);
      window.history.pushState(null, '', url);
      // Dispatch popstate so useSearchParams picks up the change
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
    [pathname, searchParams],
  );

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">{pageChildren}</div>
      <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
    </div>
  );
};

export default ArticleGridPaginated;
